/**
 * Aperçu de widget redimensionnable (configurateur).
 *
 * Rendu à la TAILLE RÉELLE qu'aura le widget sur la grille : on utilise la
 * largeur/hauteur de cellule réelles du dashboard (gridMetrics) et non une valeur
 * fixe → l'aperçu et son layout interne correspondent au rendu final. Si la taille
 * réelle dépasse la zone d'aperçu, on met à l'échelle (transform) tout en laissant
 * le contenu « voir » la vraie largeur (le responsive interne reste fidèle).
 */
import {
  ComponentType,
  PointerEvent as ReactPointerEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { getGridCellMetrics } from "./gridMetrics";
import { WidgetTypeId } from "./widgetCatalog";
import { WIDGET_COMPONENTS } from "./WidgetPreview";
import { WidgetPreviewContext } from "./widgets/WidgetShell";

/** Inset appliqué par .grid-stack-item-content (index.css) : 4px de chaque côté. */
const CELL_INSET = 4;
// Bornes globales en cellules (grille 8/12 col, lignes de 50px). Un minimum par
// type de widget (minW/minH) peut les resserrer (cf. catalogue).
const DEFAULT_MIN_W = 1;
const MAX_W = 12;
const DEFAULT_MIN_H = 2;
const MAX_H = 16;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

type ResizableWidgetPreviewProps = {
  widgetType: WidgetTypeId;
  props: Record<string, unknown>;
  w: number;
  h: number;
  onResize: (w: number, h: number) => void;
  /** Taille minimale spécifique au type de widget (cellules). */
  minW?: number;
  minH?: number;
};

export function ResizableWidgetPreview({
  widgetType,
  props,
  w,
  h,
  onResize,
  minW,
  minH,
}: ResizableWidgetPreviewProps) {
  const minWidth = Math.max(DEFAULT_MIN_W, minW ?? 0);
  const minHeight = Math.max(DEFAULT_MIN_H, minH ?? 0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = WIDGET_COMPONENTS[widgetType] as ComponentType<any>;

  const { cellWidth, cellHeight } = getGridCellMetrics();
  // Taille réelle de la carte widget = (cellules × cellule) moins l'inset.
  const trueWidth = Math.max(80, w * cellWidth - 2 * CELL_INSET);
  const trueHeight = Math.max(80, h * cellHeight - 2 * CELL_INSET);

  // Mise à l'échelle pour tenir dans la zone d'aperçu (le contenu garde sa vraie largeur).
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const available = el.clientWidth;
      setScale(available > 0 ? Math.min(1, available / trueWidth) : 1);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [trueWidth]);

  if (!Component) return null;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = w;
    const startH = h;

    const move = (e: PointerEvent) => {
      // Les déplacements écran sont divisés par l'échelle pour revenir en px réels.
      const deltaW = Math.round((e.clientX - startX) / scale / cellWidth);
      const deltaH = Math.round((e.clientY - startY) / scale / cellHeight);
      onResize(
        clamp(startW + deltaW, minWidth, MAX_W),
        clamp(startH + deltaH, minHeight, MAX_H)
      );
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div ref={containerRef} className="w-full">
      {/* Empreinte mise à l'échelle : occupe la place réelle de l'aperçu réduit. */}
      <div
        className="relative"
        style={{ width: trueWidth * scale, height: trueHeight * scale }}
      >
        <div
          // Pas de chrome ici : le widget fournit sa propre carte via WidgetShell
          // (carte ou transparent). Sinon le variant "transparent" serait masqué.
          className="absolute top-0 left-0 origin-top-left select-none"
          style={{
            width: trueWidth,
            height: trueHeight,
            transform: `scale(${scale})`,
          }}
        >
          <WidgetPreviewContext.Provider value={true}>
            <Component {...props} />
          </WidgetPreviewContext.Provider>
        </div>

        {/* Poignée de redimensionnement — même visuel que sur le dashboard. */}
        <div
          onPointerDown={handlePointerDown}
          className="widget-resize-handle bottom-1.5 right-1.5"
          title="Redimensionner"
          aria-label="Redimensionner le widget"
        />
      </div>
      <p className="text-xs text-grey-medium mt-2">{w} × {h} cellules</p>
    </div>
  );
}
